import React, { Component } from "react";
import * as d3 from "d3";
import styled from "styled-components";
import { connect } from "react-redux";

import closeSvg from "../../assets/images/close.svg";

const GraphContainer = styled.div`
  width: 100%;
  height: 80vh;
  text-align: center;
  .levels,
  .axes,
  .baseArc,
  .extBaseArc {
    stroke-dasharray: 1.5, 2;
    stroke: #1b567a;
    stroke-width: 0.5pt;
    fill: none;
  }
  .itemName {
    fill: ${props => props.theme.text};
    font-size: 10px;
  }
  @media (min-width: 1800px) {
    width: 90%;
    .levels,
    .axes,
    .baseArc,
    .extBaseArc {
      stroke-dasharray: 2.55 3.39;
      stroke-width: 1pt;
    }
    .itemName {
      font-size: 16px;
    }
  }
`;

class ZoomedGraph extends Component {
  componentDidMount() {
    this.initGraph();
  }

  componentDidUpdate(prevProps) {
    if (this.props.theme !== prevProps.theme) {
      this.initGraph();
    }
  }

  nextPrevious = () => {
    const container = d3.select(this.graph);
    let prevCircle = container.select(".prevIconCircle");

    prevCircle
      .transition()
      .duration(1000)
      .attr("r", "4.5%");
  };

  initGraph = () => {
    const container = d3.select(this.graph);

    const containerWidth = container.node().getBoundingClientRect().width;
    const containerHeight = container.node().getBoundingClientRect().height;

    let PI = Math.PI;
    const { color, gradient: graphGradient, data } = this.props;
    var margin = { top: 0, right: 0, bottom: 0, left: 0 },
      width = Math.min(740, containerWidth) - margin.left - margin.right,
      height = Math.min(740, containerHeight) - margin.top - margin.bottom;
    let cfg = {
      w: width,
      h: height,
      margin,
      levels: 3,
      plotRadius: 4, //The size of the white circles of each blob
      curveAngle: (containerWidth / 125) * 3, //Angle of the curve in degrees
      baseRadius: 720, //Radius of the 1st arc
      arcLength: containerHeight / 2.5,
      color,
      graphGradient,
      smallGraphGradient: ["#87A8D8", "#545FEA"]
    };

    if (window.innerWidth > 1800) {
      cfg.curveAngle = 18;
      cfg.plotRadius = 6;
      cfg.arcLength = containerHeight / 3.5;
    }

    var allAxis = data.values.map(function (i, j) {
      return i.axis;
    }), //Names of each axis
      total = allAxis.length, //The number of different axes (adding 2 extra for space between axes of departments and sections)
      angleSlice = cfg.curveAngle / (total - 1); //The width in radians of each "slice"

    //Scale for the radius
    var rScale = d3
      .scaleLinear()
      .range([0, cfg.arcLength])
      .domain([0, 100]);

    /////////////////////////////////////////////////////////
    //////////// Create the container SVG and g /////////////
    /////////////////////////////////////////////////////////

    //Remove whatever chart with the same id/class was present before
    container.select("svg").remove();

    //Initiate the radar chart SVG
    var svg = container
      .append("svg")
      .attr("preserveAspectRatio", "xMinYMin meet")
      .attr(
        "viewBox",
        `0 0 ${cfg.w + cfg.margin.left + cfg.margin.right} ${cfg.h +
        cfg.margin.top +
        cfg.margin.bottom} `
      )
      .attr("class", "chart");

    //Append a g element
    var g = svg
      .append("g")
      .attr(
        "transform",
        "translate(" +
        (cfg.w / 2 + cfg.margin.left) +
        "," +
        (cfg.h / 2 + cfg.margin.top) +
        ")"
      );

    ///////////////////////////////////////////////////
    ///////////// Create the radial gradient //////////
    ///////////////////////////////////////////////////

    var gradient = svg
      .append("defs")
      .append("radialGradient")
      .attr("id", "radial-gradient")
      .attr("cx", "0")
      .attr("cy", "0")
      .attr("r", cfg.baseRadius + cfg.arcLength)
      .attr("spreadMethod", "pad")
      .attr("gradientUnits", "userSpaceOnUse");

    gradient
      .append("stop")
      .attr("offset", "80%")
      .attr("stop-color", cfg.graphGradient[0]);

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", cfg.graphGradient[1]);

    /////////////////////////////////////////////////////////
    /////////////// Draw the Arc grid ///////////////////////
    /////////////////////////////////////////////////////////

    //Wrapper for the grid & axes
    var axisGrid = g
      .append("g")
      .attr("class", "axisWrapper")
      .attr(
        "transform",
        "translate(0," + (cfg.baseRadius + cfg.arcLength / 2) + ")"
      );

    //Draw the background arcs for indicating performance level
    let l = -1;
    var arc = d3
      .radialLine()
      .radius(function (d, i) {
        return cfg.baseRadius + l * (cfg.arcLength / 2);
      })
      .angle(function (d, i) {
        return (-cfg.curveAngle + 2 * i * angleSlice) * (PI / 180);
      });

    axisGrid
      .selectAll(".levels")
      .data(d3.range(1, cfg.levels + 1).reverse())
      .enter()
      .append("path")
      .attr("class", "levels")
      .attr("d", function (d, i) {
        l++;
        return arc(data.values);
      })
      .style("opacity", 0)
      .transition()
      .duration(500)
      .delay(100)
      .style("opacity", 1);

    //Arc at the top

    var topArc = d3
      .radialLine()
      .radius(cfg.baseRadius + cfg.arcLength + containerHeight / 5.5)
      .angle(function (d, i) {
        return (-cfg.curveAngle + 2 * i * angleSlice) * (PI / 180);
      });

    axisGrid
      .append("path")
      .attr("class", "baseArc")
      .attr("d", topArc(data.values))
      .style("opacity", 0)
      .transition()
      .duration(500)
      .delay(100)
      .style("opacity", 0.4);
    /////////////////////////////////////////////////////////
    //////////////////// Draw the axes //////////////////////
    /////////////////////////////////////////////////////////

    //Create the straight lines radiating outward from the center

    var axis = axisGrid
      .selectAll(".axis")
      .data(data.values)
      .enter()
      .append("g")
      .attr("class", "axis");

    //Append the axis lines
    axis
      .append("line")
      .attr("x1", function (d, i) {
        return (
          cfg.baseRadius *
          Math.cos(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("y1", function (d, i) {
        return (
          cfg.baseRadius *
          Math.sin(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("x2", function (d, i) {
        return (
          (cfg.baseRadius + cfg.arcLength) *
          Math.cos(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("y2", function (d, i) {
        if (i === Math.floor(data.values.length / 2)) {
          //Increase the length of centre axis
          return (
            (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
            Math.sin(
              (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
            )
          );
        }

        return (
          (cfg.baseRadius + cfg.arcLength) *
          Math.sin(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("class", "axes")
      .style("opacity", 0)
      .transition()
      .duration(500)
      .delay(100)
      .style("opacity", 1);

    /////////////////////////////////////////////////////////
    ///////////////////// Plot area  ////////////////////////
    /////////////////////////////////////////////////////////

    var area = d3
      .areaRadial()
      .curve(d3.curveCardinal.tension(0.5))
      .angle(function (d, i) {
        return (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180);
      })
      .innerRadius(function (d, i) {
        return cfg.baseRadius;
      })
      .outerRadius(function (d, i) {
        return cfg.baseRadius + rScale(d.value);
      });

    var areaWrapper = axisGrid.append("g").attr("class", "plottedArea");

    areaWrapper
      .append("path")
      .attr("class", "blobArea")
      .attr("d", area(data.values))
      .style("fill", "url(#radial-gradient)")
      .style("fill-opacity", 0)
      .transition()
      .duration(500)
      .delay(100)
      .style("fill-opacity", 0.8);

    /////////////////////////////////////////////////////////
    //////////////////// Base of Graph //////////////////////
    /////////////////////////////////////////////////////////

    //Colored base arc
    var baseArc = d3
      .radialLine()
      .radius(cfg.baseRadius)
      .angle(function (d, i) {
        return (-cfg.curveAngle + 2 * i * angleSlice) * (PI / 180);
      });

    axisGrid
      .append("path")
      .attr("d", baseArc(data.values))
      .style("fill", "none")
      .style("stroke", cfg.color)
      .transition()
      .delay((d, i) => {
        return 1500 + i * 20;
      })
      .style("stroke-width", "1px");

    //Circle at base of each axis
    var base = axisGrid
      .selectAll(".basePoints")
      .data(data.values)
      .enter()
      .append("g")
      .attr("class", "basePoints");

    base
      .append("circle")
      .attr("class", function (d, i) {
        return `plots plot-${i}`;
      })
      .attr("cx", function (d, i) {
        return (
          cfg.baseRadius *
          Math.cos(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("cy", function (d, i) {
        return (
          cfg.baseRadius *
          Math.sin(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .style("fill", cfg.color)
      .attr("r", 2)
      .style("opacity", 0)
      .transition()
      .duration(500)
      .delay((d, i) => {
        return 1500 + i * 20;
      })
      .style("opacity", 1);

    //Extended dashed base arc
    let extCurveAngle = cfg.curveAngle + 8;
    let extAngleSlice = (extCurveAngle * 2) / (data.values.length - 1);
    var extBaseArc = d3
      .radialLine()
      .radius(cfg.baseRadius)
      .angle(function (d, i) {
        return (-extCurveAngle + extAngleSlice * i) * (PI / 180);
      });

    axisGrid
      .append("path")
      .attr("class", "extBaseArc")
      .attr("d", extBaseArc(data.values))
      .style("opacity", 0)
      .transition()
      .duration(500)
      .delay((d, i) => {
        return 1500 + i * 20;
      })
      .style("opacity", 0.4)
      .style("stroke-width", "0.5pt");

    /////////////////////////////////////////////////////////
    ///////////////////// Plot points  //////////////////////
    /////////////////////////////////////////////////////////

    d3.select("body")
      .selectAll(".tooltip")
      .remove();

    //Create Tooltip
    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0);

    const closeIcon = axisGrid
      .append("svg:image")
      .attr("xlink:href", closeSvg)
      .attr("class", "closeIcon")
      .attr("width", 20)
      .attr("height", 20)
      .attr("transform", "translate(-10,-10)")
      .attr("x", 0)
      .attr("y", 0)
      .style("cursor", "pointer")
      .on("click", () => {
        tooltip.style("opacity", 0);
        closeIcon.attr("x", 0).attr("y", 0);
      });

    var plotPoints = axisGrid
      .selectAll(".plotPoints")
      .data(data.values)
      .enter()
      .append("g")
      .attr("class", "plotPoints")
      .on("click", d => {
        let nthPoint = d3.event.target.id;

        d3.select(`.name-${nthPoint}`).style("font-family", "dubai-bold");

        closeIcon
          .attr(
            "x",
            (cfg.baseRadius + rScale(data.values[nthPoint].value)) *
            Math.cos(
              (-cfg.curveAngle + angleSlice * 2 * nthPoint) * (PI / 180) -
              Math.PI / 2
            )
          )
          .attr(
            "y",
            (cfg.baseRadius + rScale(data.values[nthPoint].value)) *
            Math.sin(
              (-cfg.curveAngle + angleSlice * 2 * nthPoint) * (PI / 180) -
              Math.PI / 2
            )
          )
          .raise();

        tooltip
          .html(
            `<div><span class="tooltip-name">${
            d.name
            }</span><br/><span class="tooltip-title">${d.value}%</span> </div>`
          )
          .style("left", `${d3.event.pageX - 200}px`)
          .style("top", `${d3.event.pageY - 10}px`);

        /////////////////////////////////////////////////////////
        //////////////// Graph inside tooltip ///////////////////
        /////////////////////////////////////////////////////////

        // set the dimensions and margins of the graph
        let margin = { top: 2, right: 2, bottom: 15, left: 2 },
          width = 135 - margin.left - margin.right,
          height = 45 - margin.top - margin.bottom;

        // parse the date / time
        let parseTime = d3.timeParse("%d-%b-%y");

        // set the ranges
        let x = d3.scaleTime().range([0, width]);
        let y = d3.scaleLinear().range([height, 0]);

        // scale the range of the data
        x.domain(
          d3.extent(d.history, function (d) {
            return parseTime(d.date);
          })
        );
        y.domain([
          0,
          d3.max(d.history, function (d) {
            return d.close;
          })
        ]);

        // define the area
        let area = d3
          .area()
          .curve(d3.curveMonotoneX)
          .x(function (d) {
            return x(parseTime(d.date));
          })
          .y0(height)
          .y1(function (d) {
            return y(d.close);
          });

        // define the line
        var valueline = d3
          .line()
          .curve(d3.curveMonotoneX)
          .x(function (d) {
            return x(parseTime(d.date));
          })
          .y(function (d) {
            return y(d.close);
          });

        let svg = tooltip
          .append("svg")
          .attr("width", width + margin.left + margin.right)
          .attr("height", height + margin.top + margin.bottom)
          .attr(
            "viewbox",
            `0 0 ${width + margin.left + margin.right} ${height +
            margin.top +
            margin.bottom}`
          )
          .append("g")
          .attr(
            "transform",
            "translate(" + margin.left + "," + margin.top + ")"
          );

        ///////////////////////////////////////////////////
        ///////////// Create the linear gradient //////////
        ///////////////////////////////////////////////////

        svg
          .append("defs")
          .append("linearGradient")
          .attr("id", "linear-gradient")
          .attr("gradientUnits", "userSpaceOnUse")
          .attr("x1", 0)
          .attr("y1", y(0))
          .attr("x2", 0)
          .attr("y2", y(100))
          .selectAll("stop")
          .data([
            { offset: "0%", color: cfg.smallGraphGradient[0] },
            { offset: "100%", color: cfg.smallGraphGradient[1] }
          ])
          .enter()
          .append("stop")
          .attr("offset", function (d) {
            return d.offset;
          })
          .attr("stop-color", function (d) {
            return d.color;
          });

        // add the area
        svg
          .append("path")
          .data([d.history])
          .attr("class", "area")
          .attr("d", area)
          .style("fill", "url(#linear-gradient)");

        // add the valueline path.
        svg
          .append("path")
          .data([d.history])
          .attr("class", "line")
          .attr("d", valueline)
          .style("stroke", cfg.smallGraphGradient[1])
          .style("fill", "none");

        // Line joining base and 1st and last value

        let first = d.history[0].close;
        let last = d.history[d.history.length - 1].close;

        svg
          .append("line")
          .attr("x1", 0)
          .attr("y1", y(0))
          .attr("x2", 0)
          .attr("y2", y(last))
          .style("stroke", cfg.smallGraphGradient[1])
          .style("fill", "none");

        svg
          .append("line")
          .attr("x1", width)
          .attr("y1", y(0))
          .attr("x2", width)
          .attr("y2", y(first))
          .style("stroke", cfg.smallGraphGradient[1])
          .style("fill", "none");

        //Circles at two ends of axes
        svg
          .append("circle")
          .attr("cx", 0)
          .attr("cy", y(0))
          .attr("r", 2)
          .style("fill", cfg.smallGraphGradient[1]);

        svg
          .append("circle")
          .attr("cx", width)
          .attr("cy", y(0))
          .attr("r", 2)
          .style("fill", cfg.smallGraphGradient[1]);

        //Dashed line in the base
        svg
          .append("line")
          .attr("x1", 0)
          .attr("y1", y(0))
          .attr("x2", width)
          .attr("y2", y(0))
          .style("stroke-dasharray", "2, 2")
          .style("stroke", cfg.smallGraphGradient[1])
          .style("fill", "none")
          .style("stroke-width", "0.5px");

        // text below the graph
        svg
          .append("text")
          .attr("text-anchor", "start")
          .attr("x", 0)
          .attr("y", y(-40))
          .attr("class", "smallgraph-marking")
          .text("2018");

        svg
          .append("text")
          .attr("text-anchor", "end")
          .attr("x", width)
          .attr("y", y(-40))
          .attr("class", "smallgraph-marking")
          .text("2019");

        //Show the tooltip
        tooltip
          .transition()
          .duration(200)
          .style("opacity", 1);
      });

    plotPoints
      .append("circle")
      .attr("class", function (d, i) {
        return `plots plot-${i}`;
      })
      .attr("id", function (d, i) {
        return i;
      })
      .attr("cx", function (d, i) {
        return (
          (cfg.baseRadius + rScale(d.value)) *
          Math.cos(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("cy", function (d, i) {
        return (
          (cfg.baseRadius + rScale(d.value)) *
          Math.sin(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .style("fill", "#fff")
      .style("cursor", "pointer")
      .attr("r", 0)
      .transition()
      .duration(500)
      .delay(100)
      .attr("r", cfg.plotRadius);

    /////////////////////////////////////////////////////////
    //////////// Current Item Name and Icon /////////////////
    /////////////////////////////////////////////////////////

    var itemDetails = axisGrid.append("g").attr("class", "itemDetails");

    itemDetails
      .append("text")
      .attr("class", "itemName")
      .attr("x", 0)
      .attr("y", -(cfg.baseRadius + cfg.arcLength + containerHeight / 12))
      .style("text-anchor", "middle")
      .attr("startOffset", "50%")
      .style("opacity", 0)
      .transition()
      .duration(500)
      .delay(1000)
      .style("opacity", 0.5)
      .text(data.itemName);

    itemDetails
      .append("circle")
      .attr("class", "itemIconCircle")
      .attr("r", "4.5%")
      .attr("cx", 0)
      .attr("cy", -(cfg.baseRadius + cfg.arcLength + containerHeight / 5.5))
      .style("fill", cfg.color);

    itemDetails
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("fill", "white")
      .attr("class", "fa")
      .attr("x", 0)
      .attr("y", -(cfg.baseRadius + cfg.arcLength + containerHeight / 5.5))
      .attr("font-size", "250%")
      .text(data.icon);

    /////////////////////////////////////////////////////////
    ////////// Next/Prev Item Name and Icon /////////////////
    /////////////////////////////////////////////////////////
    //Previous
    itemDetails
      .append("circle")
      .attr("class", "prevIconCircle")
      .style("fill", cfg.color)
      .style("opacity", 1)
      .attr("r", "4.5%")
      .attr(
        "cx",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos(
          (-cfg.curveAngle + angleSlice * 8) * (PI / 180) - Math.PI / 2
        )
      )
      .attr(
        "cy",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin(
          (-cfg.curveAngle + angleSlice * 8) * (PI / 180) - Math.PI / 2
        )
      )
      .transition()
      .duration(1000)
      .style("opacity", 0.4)
      .attr(
        "cx",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos((-cfg.curveAngle + angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .attr(
        "cy",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin((-cfg.curveAngle + angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .attr("r", "2.3%");

    itemDetails
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("fill", "white")
      .attr("font-size", "250%")
      .text(data.prevItem.icon)
      .attr("class", "fa ")
      .attr(
        "x",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos(
          (-cfg.curveAngle + angleSlice * 8) * (PI / 180) - Math.PI / 2
        )
      )
      .attr(
        "y",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin(
          (-cfg.curveAngle + angleSlice * 8) * (PI / 180) - Math.PI / 2
        )
      )
      .transition()
      .duration(1000)
      .attr(
        "x",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos((-cfg.curveAngle + angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .attr(
        "y",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin((-cfg.curveAngle + angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .attr("font-size", "150%")
      .style("opacity", 0.4);

    //Next
    itemDetails
      .append("circle")
      .attr("class", "prevIconCircle")
      .attr("r", "4.5%")
      .attr(
        "cx",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos(
          (-cfg.curveAngle + angleSlice * 16) * (PI / 180) - Math.PI / 2
        )
      )
      .attr(
        "cy",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin(
          (-cfg.curveAngle + angleSlice * 16) * (PI / 180) - Math.PI / 2
        )
      )
      .style("fill", cfg.color)
      .style("opacity", 1)
      .transition()
      .duration(1000)
      .attr("r", "2.3%")
      .attr(
        "cx",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos((cfg.curveAngle - angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .attr(
        "cy",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin((cfg.curveAngle - angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .style("opacity", 0.4);

    itemDetails
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("fill", "white")
      .attr("font-size", "250%")
      .attr("class", "fa")
      .attr(
        "x",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos(
          (-cfg.curveAngle + angleSlice * 16) * (PI / 180) - Math.PI / 2
        )
      )
      .attr(
        "y",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin(
          (-cfg.curveAngle + angleSlice * 16) * (PI / 180) - Math.PI / 2
        )
      )
      .transition()
      .duration(1000)
      .attr(
        "x",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.cos((cfg.curveAngle - angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .attr(
        "y",
        (cfg.baseRadius + cfg.arcLength + containerHeight / 5.5) *
        Math.sin((cfg.curveAngle - angleSlice) * (PI / 180) - Math.PI / 2)
      )
      .attr("font-size", "150%")
      .text(data.nextItem.icon)
      .style("opacity", 0.4);

    /////////////////////////////////////////////////////////
    /////////////// Individual value names //////////////////
    /////////////////////////////////////////////////////////

    axis
      .append("text")
      .attr("class", function (d, i) {
        return `names name-${i}`;
      })
      .attr("x", function (d, i) {
        return (
          cfg.baseRadius *
          Math.cos(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("y", function (d, i) {
        return (
          (cfg.baseRadius - 15) *
          Math.sin(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )
        );
      })
      .attr("fill", cfg.color)
      .attr("text-anchor", "end")
      .attr("transform", function (d, i) {
        return `rotate(-45,${cfg.baseRadius *
          Math.cos(
            (-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2
          )}, ${(cfg.baseRadius - 15) * Math.sin((-cfg.curveAngle + angleSlice * 2 * i) * (PI / 180) - Math.PI / 2)})`;
      })
      .style("font-size", 12)
      .transition()
      .duration(500)
      .delay((d, i) => {
        return 1500 + i * 20;
      })
      .text(function (d, i) {
        return d.name;
      });

    /////////////////////////////////////////////////////////
    ////////////////// Outside Click ////////////////////////
    /////////////////////////////////////////////////////////

    d3.select("body").on("click", function () {
      let isOnPoints = false;

      plotPoints.nodes().map(item => {
        if (d3.event.target.parentNode === item) {
          isOnPoints = true;
        }
      });
      if (!isOnPoints) {
        tooltip.style("opacity", 0);
        closeIcon.attr("x", 0).attr("y", 0);

        d3.selectAll(".names").style("font-family", "dubai-regular");
      }
    });
  };

  render() {
    return <GraphContainer ref={el => (this.graph = el)} className="graph" />;
  }
}

const mapStateToProps = state => ({
  theme: state.themes.theme
});

export default connect(
  mapStateToProps,
  null
)(ZoomedGraph);